<script setup lang="ts">
import { ChevronDown, ChevronRight, CircleUserRound, Menu, X } from 'lucide-vue-next';
import { computed, defineAsyncComponent, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { RouterLink, useRoute } from 'vue-router';

import SearchLauncher from '#/components/fo/analysis/search/SearchLauncher.vue';
import CreditBadge from '#/components/fo/analysis/shared/CreditBadge.vue';
import BrandLogo from '#/components/layout/BrandLogo.vue';
import NavLinkItem from '#/components/layout/NavLinkItem.vue';
import { useGnbMenu } from '#/composables/useGnbMenu';
import { useLogout } from '#/composables/useLogout';
import { useMobileNav } from '#/composables/useMobileNav';
import { brand } from '#/config/brand';
import type { NavLink } from '#/config/nav';
import { LANDING_START_ROUTE } from '#/data/analysis/landingAxes';
import { WORKSPACE_NAV, type WorkspaceSubItem } from '#/data/analysis/workspaceNav';
import { useAuthStore } from '#/stores/auth';
import { useUnifiedSearchStore } from '#/stores/unifiedSearch';

/** 통합 검색 패널(078) — 첫 열림 때만 청크를 받는다(셸 1회 마운트). */
const SearchPanel = defineAsyncComponent(
  () => import('#/components/fo/analysis/search/SearchPanel.vue'),
);

/**
 * WorkspaceShell — 2차 AI 입시분석 워크스페이스의 상단 GNB + 본문 프레임.
 *
 * ⚠️ 도메인 전용 셸이라 components/fo/analysis/ 소유(components/layout·ui 는 도메인 무지 원칙 —
 *    docs/frontend-domain-structure-proposal.md §69·71). 1차 커머스 AppHeader 와 별개 셸이며
 *    서브도메인 배포 시 이 셸이 루트가 된다.
 *
 * ## UI 구조 = 커머스 AppHeader 동형 (사용자 요청 — 두 축 헤더 일관성)
 *
 * 2026-10 커머스 랜딩 개편의 **알약(pill) 헤더**를 그대로 가져왔다: [로고 알약] [대분류 5개 + 자료실 칩이 든
 * 메뉴 알약] [그린 로그인 알약] 세 덩어리가 페이지 바탕 위에 떠 있고, 전체 메뉴는 헤더 아래 **둥근 흰 패널
 * 1장**으로 열린다. 동작·마크업 규약은 AppHeader 와 같고 **색만 그린**이다:
 *  - 열림 상태는 `useGnbMenu`(hover 유예·클릭 고정·외부 클릭/ESC 닫기), 모바일 열림은 `useMobileNav`
 *    (lg 이상으로 넓어지면 자동 닫힘) 재사용.
 *  - 항목 렌더는 `NavLinkItem`(panel-ws / mobile) 재사용 — 워크스페이스 항목(WorkspaceSubItem)을
 *    NavLink 로 변환하는 어댑터만 이 파일이 소유한다(active→배지 없음·soon→'준비중'+disabled).
 *  - 커머스의 "대학 검색" 칩 자리에는 축 전환(자료실·리포트) 칩, 패널 하단 CTA 는 리포트 샘플 대신
 *    **내 입시카드 입력**(LANDING_START_ROUTE). 검색은 메가메뉴가 아니라 통합 검색(078)이 맡는다 —
 *    우측 런처 알약·⌘K 가 `stores/unifiedSearch` 를 열고 패널은 이 셸에 1회 마운트된다.
 *  - body 텔레포트 dim(z-gnb-dim) 동일.
 *
 * 색은 `--ws-*`(Figma 랜딩 2026-09 고정값, tokens.workspace.css) — 커머스 `--lp-navy`→`ws-ink`,
 * `--lp-gold`→`ws-green`, `--lp-cream`→`ws-box`, `--lp-gray-2`→`ws-chip`, `--lp-page-glass`→`ws-page-glass`
 * 로 1:1 치환했다.
 *
 * GNB 데이터는 data/analysis/workspaceNav.ts 단일 SSoT — 여기서는 표시용 변환만 한다.
 * 본문은 default slot(페이지가 <PageShell variant="workspace"> 로 자기 폭·여백 소유).
 */
const auth = useAuthStore();
const route = useRoute();
const { logout } = useLogout();
const { isOpen: isMobileOpen, close: closeMobile, toggle: toggleMobile } = useMobileNav();
const {
  openLabel,
  isOpen: isGroupOpen,
  toggle: toggleGroup,
  hoverOpen,
  hoverClose,
  hoverKeep,
  close,
} = useGnbMenu();

/** 모바일 아코디언에서 펼친 대분류(null=전부 접힘). 데스크톱 패널 상태와 독립. */
const expandedMobile = ref<string | null>(null);

function toggleMobileGroup(label: string): void {
  expandedMobile.value = expandedMobile.value === label ? null : label;
}

/**
 * WorkspaceSubItem → NavLink 어댑터 — NavLinkItem(커머스 공용 렌더)에 태우기 위한 변환.
 * active=route 이동(배지 없음) / soon=disabled + '준비중' 뉴트럴 배지.
 *
 * 활성 항목에 '이용 가능' 배지를 달지 않는 이유: 이용 가능은 **기본 상태**라 굳이 표시할 정보가
 * 아니다. 예외(준비중)만 표시하면 눈이 갈 곳이 줄고, 활성 항목이 늘어날수록 배지 반복으로
 * 생기는 시각 소음도 없다.
 */
function toNavLink(item: WorkspaceSubItem): NavLink {
  if (item.status === 'active' && item.route) {
    return {
      label: item.label,
      desc: item.desc ?? '',
      auth: 'always',
      route: item.route,
    };
  }
  return {
    label: item.label,
    desc: item.desc ?? '',
    auth: 'always',
    badge: { text: '준비중', tone: 'soon' },
    disabled: true,
  };
}

/** 메가메뉴 컬럼 — 대분류 라벨 + 변환된 항목. */
const groups = computed(() =>
  WORKSPACE_NAV.map((g) => ({ label: g.label, items: g.items.map(toNavLink) })),
);

/**
 * 컬럼 수 → Tailwind 클래스(AppHeader 와 동일한 정적 맵 — JIT 스캔 때문에 동적 조립 불가).
 * 대분류 추가·보류 해제 시 그리드가 따라간다.
 */
const GRID_COLS: Record<number, string> = {
  1: 'lg:grid-cols-1',
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
  5: 'lg:grid-cols-5',
};

/**
 * 항목 route 와 현재 라우트의 일치 판정 — pathname 정확 일치·하위 경로 접두 일치(AppHeader 규칙)에
 * 더해 항목이 `?kind=` 쿼리를 가지면 그 값까지 일치해야 한다(065 보관함 5항목이 각각 활성 표시).
 * 항목 route 는 `#앵커`·`?쿼리` 를 품을 수 있어 `URL` 로 분해한다(문자열 비교는 절대 일치하지 않는다).
 */
function isItemActive(itemRoute: string): boolean {
  const target = new URL(itemRoute, 'https://workspace.local');
  const pathMatches =
    route.path === target.pathname || route.path.startsWith(`${target.pathname}/`);
  if (!pathMatches) return false;
  const kind = target.searchParams.get('kind');
  return kind === null || route.query.kind === kind;
}

/** 현재 라우트가 속한 대분류 — 활성 표시용. */
function isGroupActive(group: { items: NavLink[] }): boolean {
  return group.items.some((i) => !!i.route && !i.disabled && isItemActive(i.route));
}

/** 링크 이동 후 열린 패널·모바일 메뉴 정리. */
function onNavigate(): void {
  close();
  closeMobile();
}

/**
 * 로그아웃 후 워크스페이스 로컬 홈(/analysis)으로 복귀 — 커머스('/')와 대칭.
 * 정리 콜백은 onNavigate — 모바일 패널만 닫으면 클릭 고정(pinned)된 메가메뉴·dim 이
 * 동일 라우트 복귀(예: /analysis 에서 로그아웃) 시 그대로 남는다.
 */
function onLogout() {
  return logout(brand.crossLinks.analysis, onNavigate);
}

/** 로그인 목적지 — 로그인 후 워크스페이스로 복귀. */
const loginRoute = { path: '/login', query: { redirect: brand.crossLinks.analysis } };

// ── 통합 검색(078) — 진입점(런처·⌘K)은 스토어만 연다. 열기 전 메가메뉴·모바일 메뉴를 닫는다 ──
const search = useUnifiedSearchStore();
/** 패널 컴포넌트는 첫 열림 이후에만 렌더(지연 청크 — 랜딩 첫 로드에 무게를 싣지 않는다). */
const searchMounted = ref(false);
watch(
  () => search.isOpen,
  (open) => {
    if (open) {
      searchMounted.value = true;
      onNavigate();
    }
  },
);

/** ⌘K / Ctrl+K — 입력 중(input·textarea·contenteditable)에는 가로채지 않는다. */
function onSearchShortcut(e: KeyboardEvent): void {
  if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'k') return;
  const el = e.target as HTMLElement | null;
  if (el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName))) return;
  e.preventDefault();
  search.open('shortcut');
}

onMounted(() => window.addEventListener('keydown', onSearchShortcut));
onBeforeUnmount(() => window.removeEventListener('keydown', onSearchShortcut));

// 모바일 메뉴를 닫으면 펼쳐둔 아코디언도 초기화 — 다시 열 때 이전 상태가 남지 않게 한다.
watch(isMobileOpen, (open) => {
  if (!open) expandedMobile.value = null;
});
</script>

<template>
  <div class="flex min-h-screen flex-col bg-bg">
    <header class="sticky top-0 z-header bg-ws-page-glass backdrop-blur-[10px]">
      <!-- 헤더 내부 클릭은 외부 클릭 닫기(document 리스너)로 새지 않게 차단 -->
      <div
        class="mx-auto flex h-header max-w-page-workspace items-center gap-2 px-[10px] md:px-[15px] lg:gap-[10px] xl:px-[15px]"
        @click.stop
      >
        <!-- 로고 알약 → 2차 홈. 'AI 입시분석' 배지로 축을 드러낸다(폭이 빠듯한 lg~xl 구간은 숨김). -->
        <RouterLink
          to="/analysis"
          class="flex h-12 flex-none items-center gap-3 rounded-full bg-white px-4 shadow-sm md:px-5"
          aria-label="AI 입시분석 홈"
          @click="onNavigate"
        >
          <BrandLogo />
          <span
            class="hidden rounded-full bg-ws-green px-2.5 py-1 text-[11px] font-extrabold tracking-tight text-white md:inline-block lg:hidden 2xl:inline-block"
            >AI 입시분석</span
          >
        </RouterLink>

        <!-- 메뉴 알약(lg↑): 대분류 5개 + 자료실·리포트 칩(축 전환) -->
        <nav
          class="hidden h-12 min-w-0 flex-1 items-center rounded-full bg-white p-[6px] shadow-sm lg:flex"
          aria-label="AI 입시분석 메뉴"
          @mouseleave="hoverClose"
        >
          <button
            v-for="group in groups"
            :key="group.label"
            type="button"
            class="flex h-9 min-w-0 flex-1 items-center justify-center gap-[6px] whitespace-nowrap rounded-full px-2 text-[12px] font-bold leading-none transition-colors xl:text-[13px]"
            :class="
              isGroupActive(group) || isGroupOpen(group.label)
                ? 'text-ws-green'
                : 'text-ws-ink/70 hover:text-ws-ink'
            "
            :aria-expanded="isGroupOpen(group.label)"
            aria-haspopup="true"
            aria-controls="ws-gnb-mega-panel"
            @mouseenter="hoverOpen(group.label)"
            @focus="hoverOpen(group.label)"
            @click="toggleGroup(group.label)"
          >
            {{ group.label }}
            <ChevronDown
              :size="12"
              :stroke-width="1.6"
              class="flex-none transition-transform"
              :class="isGroupOpen(group.label) ? 'rotate-180' : ''"
            />
          </button>
          <!-- 축 전환 칩 — 커머스(자료실·리포트)로 복귀. 커머스 헤더의 '대학 검색' 칩 자리. -->
          <RouterLink
            :to="brand.crossLinks.commerce"
            class="ml-[6px] flex h-9 w-[150px] flex-none items-center justify-center gap-[6px] whitespace-nowrap rounded-full bg-ws-chip px-4 text-[12px] font-bold text-ws-muted transition-colors hover:text-ws-green xl:w-[184px]"
            @click="onNavigate"
          >
            자료실 · 리포트
            <ChevronRight :size="14" :stroke-width="2.2" class="flex-none" />
          </RouterLink>
        </nav>

        <!-- 우측: 이용권 잔량 + 인증 분기(그린 알약) + 햄버거 -->
        <div class="ml-auto flex flex-none items-center gap-2 lg:ml-0 lg:gap-[10px]">
          <!-- 통합 검색(078) — 980~1439 는 메뉴 알약이 빠듯해 숨김(⌘K·히어로 검색창 유지) -->
          <SearchLauncher />
          <!-- AI 이용권 잔량(정시 해석·수시 카드 공용 풀) — 쿼터 활성 시에만 렌더 -->
          <CreditBadge />

          <template v-if="auth.isAuthenticated">
            <!-- 마이페이지 — 그린 알약(커머스 골드 알약 자리) -->
            <RouterLink
              to="/mypage/library"
              class="hidden h-12 items-center justify-center gap-[10px] whitespace-nowrap rounded-full bg-ws-green px-6 text-[12px] font-bold text-white shadow-sm transition-colors hover:bg-ws-green-deep lg:inline-flex"
              @click="onNavigate"
            >
              <CircleUserRound :size="20" :stroke-width="1.7" />
              마이페이지
            </RouterLink>
            <!-- 로그아웃 — 흰 알약 -->
            <button
              type="button"
              class="hidden h-12 items-center whitespace-nowrap rounded-full bg-white px-5 text-[12px] font-bold text-ws-ink shadow-sm transition-colors hover:text-ws-green lg:inline-flex"
              @click="onLogout"
            >
              로그아웃
            </button>
          </template>
          <!-- 비로그인: 로그인 1알약(워크스페이스는 회원가입 유틸이 없어 라벨 1개) -->
          <RouterLink
            v-else
            :to="loginRoute"
            class="hidden h-12 items-center justify-center gap-[10px] whitespace-nowrap rounded-full bg-ws-green px-6 text-[12px] font-bold text-white shadow-sm transition-colors hover:bg-ws-green-deep lg:inline-flex xl:w-[196px]"
            @click="onNavigate"
          >
            <CircleUserRound :size="20" :stroke-width="1.7" />
            로그인
          </RouterLink>

          <!-- 햄버거(lg 미만) — 둥근 흰 버튼 -->
          <button
            type="button"
            class="flex h-12 w-12 items-center justify-center rounded-full bg-white text-ws-ink shadow-sm lg:hidden"
            :aria-label="isMobileOpen ? '메뉴 닫기' : '메뉴 열기'"
            :aria-expanded="isMobileOpen"
            @click="toggleMobile"
          >
            <X v-if="isMobileOpen" :size="22" :stroke-width="1.8" />
            <Menu v-else :size="22" :stroke-width="1.8" />
          </button>
        </div>
      </div>

      <!--
        전체 메뉴 패널(lg↑) — v-for 밖 단일 요소. 헤더가 sticky(=positioned)라 `top-full` 이 헤더 하단에
        붙는다. 배경은 투명·전폭이고 **카드 1장**(둥근 32px·흰색·그림자)이 헤더 알약과 같은 좌우선에 놓인다.
        패널은 헤더 안에 있어야 헤더의 stacking context 를 타고 dim(z-gnb-dim) 위로 올라간다(AppHeader 동일).
      -->
      <div
        v-show="openLabel !== null"
        id="ws-gnb-mega-panel"
        class="absolute inset-x-0 top-full hidden pt-1 lg:block"
        @mouseenter="hoverKeep"
        @mouseleave="hoverClose"
        @click.stop
      >
        <div class="mx-auto max-w-page-workspace px-[15px]">
          <div
            class="rounded-[32px] bg-white px-10 pb-8 pt-9 shadow-[0_24px_60px_rgb(16_40_30/0.16)]"
          >
            <!-- 머리: 아이브로·제목 / 닫기 -->
            <div class="flex items-center justify-between gap-6 border-b border-line pb-6">
              <div class="min-w-0">
                <p class="text-[12px] font-bold text-ws-green">{{ brand.name }} · AI 입시분석</p>
                <p class="mt-2 text-[24px] font-bold leading-tight tracking-[-0.02em] text-ws-ink">
                  내 성적으로 보는 지원 전략, 한눈에
                </p>
              </div>
              <button
                type="button"
                class="flex h-9 w-9 flex-none items-center justify-center rounded-full text-ws-ink transition-colors hover:bg-ws-chip"
                aria-label="메뉴 닫기"
                @click="close"
              >
                <X :size="20" :stroke-width="1.6" />
              </button>
            </div>

            <!-- 대분류 컬럼 -->
            <div class="grid gap-8 py-8" :class="GRID_COLS[groups.length]">
              <section v-for="(group, idx) in groups" :key="`panel-${group.label}`" class="min-w-0">
                <!-- 대분류 라벨은 <p> — <h2> 로 쓰면 헤더가 페이지 제목 아웃라인에 끼어든다. -->
                <p
                  :id="`ws-gnb-col-${idx}`"
                  class="border-b-2 pb-4 text-[18px] font-bold tracking-[-0.01em] text-ws-ink transition-colors"
                  :class="isGroupOpen(group.label) ? 'border-ws-green' : 'border-ws-green/50'"
                >
                  {{ group.label }}
                </p>
                <ul class="mt-6 space-y-5" :aria-labelledby="`ws-gnb-col-${idx}`">
                  <li v-for="link in group.items" :key="link.label">
                    <NavLinkItem :link="link" variant="panel-ws" @navigate="onNavigate" />
                  </li>
                </ul>
              </section>
            </div>

            <!-- 하단 CTA 스트립 — 내 입시카드 입력(랜딩 [시작하기] 와 같은 목적지) -->
            <div class="flex items-center justify-between gap-6 rounded-2xl bg-ws-box p-5">
              <div>
                <b class="text-[16px] font-bold text-ws-ink">어디서부터 시작할지 고민된다면</b>
                <p class="mt-2 text-[14px] text-ws-muted">
                  내 입시카드에 성적을 입력하면 정시·수시 판정과 AI 종합분석까지 이어집니다.
                </p>
              </div>
              <RouterLink
                :to="LANDING_START_ROUTE"
                class="inline-flex h-12 flex-none items-center gap-3 rounded-full bg-ws-green px-6 text-[14px] font-bold text-white transition-colors hover:bg-ws-green-deep"
                @click="onNavigate"
              >
                내 입시카드 입력하기
                <svg class="h-[7px] w-[31px]" viewBox="0 0 31 7" fill="none" aria-hidden="true">
                  <path
                    d="M27.37.5 30.5 3.68 27.37 6.5M30.5 3.68h-30"
                    stroke="currentColor"
                    stroke-linecap="round"
                  />
                </svg>
              </RouterLink>
            </div>
          </div>
        </div>
      </div>

      <!-- 모바일 패널(lg 미만): 둥근 카드 — 로그인(또는 마이페이지)·자료실 2버튼 · 대분류 아코디언 · 로그아웃 -->
      <nav
        v-show="isMobileOpen"
        class="absolute inset-x-0 top-header px-[10px] pt-1 md:px-[15px] lg:hidden"
        aria-label="AI 입시분석 메뉴(모바일)"
        @click.stop
      >
        <div
          class="max-h-[calc(100vh-theme(spacing.header)-16px)] overflow-y-auto rounded-[24px] bg-white px-5 pb-6 pt-5 shadow-[0_24px_60px_rgb(16_40_30/0.18)]"
        >
          <p class="text-[12px] font-bold text-ws-green">{{ brand.name }} · AI 입시분석</p>
          <p class="mt-1 text-[20px] font-bold leading-snug tracking-[-0.02em] text-ws-ink">
            내 성적으로 보는 지원 전략, 한눈에
          </p>

          <!-- 2버튼: [로그인 | 마이페이지] + [자료실 · 리포트] -->
          <div class="mt-5 grid grid-cols-2 gap-3">
            <RouterLink
              v-if="auth.isAuthenticated"
              to="/mypage/library"
              class="flex h-11 items-center justify-center rounded-full bg-ws-ink text-[14px] font-bold text-white"
              @click="onNavigate"
            >
              마이페이지
            </RouterLink>
            <RouterLink
              v-else
              :to="loginRoute"
              class="flex h-11 items-center justify-center rounded-full bg-ws-ink text-[14px] font-bold text-white"
              @click="onNavigate"
            >
              로그인
            </RouterLink>
            <RouterLink
              :to="brand.crossLinks.commerce"
              class="flex h-11 items-center justify-center gap-1 rounded-full bg-ws-green text-[14px] font-bold text-white"
              @click="onNavigate"
            >
              자료실 · 리포트
              <ChevronRight :size="16" :stroke-width="2.4" />
            </RouterLink>
          </div>

          <!-- 대분류 아코디언 -->
          <div v-for="group in groups" :key="`m-${group.label}`" class="border-b border-line">
            <button
              type="button"
              class="flex w-full items-center justify-between py-[15px] text-left text-[16px] font-bold transition-colors"
              :class="isGroupActive(group) ? 'text-ws-green' : 'text-ws-ink'"
              :aria-expanded="expandedMobile === group.label"
              @click="toggleMobileGroup(group.label)"
            >
              {{ group.label }}
              <ChevronDown
                :size="18"
                :stroke-width="2"
                class="transition-transform"
                :class="
                  expandedMobile === group.label ? 'rotate-180 text-ws-green' : 'text-ws-ink/60'
                "
              />
            </button>
            <div
              v-show="expandedMobile === group.label"
              class="mb-4 rounded-2xl bg-ws-box px-4 py-2"
            >
              <NavLinkItem
                v-for="link in group.items"
                :key="`m-${link.label}`"
                :link="link"
                variant="mobile"
                @navigate="onNavigate"
              />
            </div>
          </div>

          <button
            v-if="auth.isAuthenticated"
            type="button"
            class="border-ws-ink/20 mt-4 flex h-11 w-full items-center justify-center rounded-full border bg-white text-[14px] font-bold text-ws-ink"
            @click="onLogout"
          >
            로그아웃
          </button>
        </div>
      </nav>
    </header>

    <main class="flex-1">
      <slot />
    </main>

    <!-- 푸터는 레이아웃이 주입(브랜드 공유 AppFooter). 셸은 자리만 제공 — layout→도메인 의존 회피. -->
    <slot name="footer" />

    <!-- 통합 검색 패널(078) — 셸 1회 마운트, 진입점은 stores/unifiedSearch 만 호출 -->
    <SearchPanel v-if="searchMounted" />

    <!-- 메가메뉴 배경 dim — 헤더의 backdrop-blur 가 fixed 기준을 바꾸므로 body 텔레포트(AppHeader 동일 함정 주석 참조). -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition-opacity duration-200 motion-reduce:transition-none"
        enter-from-class="opacity-0"
        leave-active-class="transition-opacity duration-150 motion-reduce:transition-none"
        leave-to-class="opacity-0"
      >
        <div
          v-if="openLabel !== null"
          class="fixed inset-0 z-gnb-dim hidden bg-overlay lg:block"
          aria-hidden="true"
          @click="close"
        />
      </Transition>
    </Teleport>
  </div>
</template>
